import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Eye, EyeOff, LogIn, Loader2, Shield, Phone, MessageCircle,
  Zap, BarChart3, Package, Brain, TrendingUp, Archive, FileText,
  Download, Smartphone, X, QrCode, PlayCircle
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import PresentationVideo from "../components/PresentationVideo";

const LOGO_URL = "/logo_cmes.jpg";
const BG_IMAGE = "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=90";

const FEATURES = [
  { icon: Shield, text: "PHARMA P\u00c9RIM\u00c9S : Gestion des p\u00e9rim\u00e9s, destructions s\u00e9curis\u00e9es et attestations", color: "#00C78F" },
  { icon: FileText, text: "PHARMA ASSURANCES : Facturation assurances, cr\u00e9ances et recouvrement automatis\u00e9", color: "#3B82F6" },
  { icon: TrendingUp, text: "PHARMA ASSURANCES : Pipeline Kanban temps r\u00e9el et suivi GPS des agents commerciaux", color: "#3B82F6" },
  { icon: Brain, text: "PHARMA DISPENSATION : Copilot IA pharmacien, analyse d'ordonnances et interactions", color: "#8B5CF6" },
  { icon: Zap, text: "PHARMA DISPENSATION : Profil Patient Virtuel (Digital Twin) et pharmacovigilance", color: "#8B5CF6" },
  { icon: Archive, text: "Tra\u00e7abilit\u00e9 compl\u00e8te, chiffrement AES et archivage s\u00e9curis\u00e9", color: "#FFC300" },
  { icon: BarChart3, text: "Rapports IA, pr\u00e9visions de tr\u00e9sorerie et d\u00e9tection d'anomalies", color: "#FFC300" },
];

const LoginPage = () => {
  const { login, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  // 2FA OTP state
  const [otpStep, setOtpStep] = useState(null); // null | { challengeId, emailHint, message }
  const [otpCode, setOtpCode] = useState("");

  useEffect(() => {
    if (searchParams.get("timeout") === "1") {
      toast.info("Session expiree. Veuillez vous reconnecter.", { duration: 5000 });
    }
  }, [searchParams]);

  // PWA Install prompt
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // Also show manual instructions banner after 2s if no prompt fired
    const timer = setTimeout(() => { if (!standalone) setShowInstallBanner(true); }, 2000);
    return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(timer); };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === "accepted") {
        toast.success("Application installee avec succès !");
        setShowInstallBanner(false);
      }
      setInstallPrompt(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      // 2FA challenge - switch UI to OTP step
      if (result?.twofaRequired) {
        setOtpStep({ challengeId: result.challengeId, emailHint: result.emailHint, message: result.message });
        toast.info(`Un code a ete envoye a ${result.emailHint}. Saisissez-le pour terminer la connexion.`, { duration: 7000 });
        setLoading(false);
        return;
      }
      const user = result;
      toast.success(`Bienvenue, ${user.full_name}!`);
      if (user.role === "coursier" || user.role === "agent commercial") {
        navigate("/coursier");
      } else if (user.role === "assurance") {
        navigate("/assurance");
      } else {
        navigate("/hub");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Identifiants incorrects";
      const status = error.response?.status;
      if (status === 403) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error("Le code doit comporter 6 chiffres");
      return;
    }
    setLoading(true);
    try {
      const user = await verifyOtp(otpStep.challengeId, otpCode);
      toast.success(`Bienvenue, ${user.full_name}!`);
      if (user.role === "coursier" || user.role === "agent commercial") {
        navigate("/coursier");
      } else if (user.role === "assurance") {
        navigate("/assurance");
      } else {
        navigate("/hub");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Video Modal */}
      {showVideo && <PresentationVideo onClose={() => setShowVideo(false)} autoPlay={true} />}

      {/* Left Panel - Hero with pharmaceutical image */}
      <div
        className="hidden lg:flex lg:w-[55%] relative overflow-hidden"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Gradient overlay using logo colors */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, #025946 0%, #0F4C44 50%, #0A1F1B 100%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between px-14 py-10 w-full overflow-y-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {/* Top: Logo + App Name side by side */}
          <div className="flex items-center gap-4 mb-4">
            <img
              src={LOGO_URL}
              alt="CMES"
              className="h-14 w-auto drop-shadow-2xl flex-shrink-0"
              data-testid="login-logo"
            />
            <div>
              <h1 className="text-3xl xl:text-4xl font-bold leading-none" style={{ color: "#fff", fontFamily: "'Cormorant Garamond', 'Georgia', serif", letterSpacing: "-0.01em" }}>
                <span style={{ color: "#fff" }}>CMES</span>{" "}
                <span style={{ color: "#C9A25E" }}>Pharma Intel</span>
              </h1>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] mt-2" style={{ color: "#C9A25E" }}>
                ─── Trois services, une seule vision
              </p>
            </div>
          </div>

          {/* Welcome message */}
          <p className="text-2xl xl:text-3xl font-light italic mb-6" style={{ color: "#C9A25E", fontFamily: "'Cormorant Garamond', serif" }}>
            Bienvenue dans notre vision am&eacute;lior&eacute;e du secteur pharmaceutique
          </p>

          {/* Citations — prominent, before features */}
          <div className="space-y-5 mb-10">
            <div className="relative p-7 xl:p-8 rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(201,162,94,0.18) 0%, rgba(201,162,94,0.06) 100%)", borderLeft: "4px solid #C9A25E", boxShadow: "0 10px 40px -10px rgba(201,162,94,0.25)" }}>
              <span aria-hidden="true" className="absolute -top-4 left-4 text-[100px] leading-none font-serif select-none pointer-events-none" style={{ color: "rgba(201,162,94,0.18)", fontFamily: "'Cormorant Garamond', serif" }}>“</span>
              <p className="relative text-xl xl:text-2xl 2xl:text-3xl italic leading-relaxed font-light" style={{ color: "#fff", fontFamily: "'Cormorant Garamond', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                Cela semble toujours impossible, jusqu'&agrave; ce qu'on le fasse.
              </p>
              <p className="relative text-sm xl:text-base mt-4 font-extrabold uppercase tracking-[0.25em]" style={{ color: "#C9A25E" }}>— Nelson Mandela</p>
            </div>
            <div className="relative p-7 xl:p-8 rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)", borderLeft: "4px solid rgba(255,255,255,0.55)", boxShadow: "0 10px 40px -10px rgba(255,255,255,0.10)" }}>
              <span aria-hidden="true" className="absolute -top-4 left-4 text-[100px] leading-none font-serif select-none pointer-events-none" style={{ color: "rgba(255,255,255,0.10)", fontFamily: "'Cormorant Garamond', serif" }}>“</span>
              <p className="relative text-xl xl:text-2xl 2xl:text-3xl italic leading-relaxed font-light" style={{ color: "#fff", fontFamily: "'Cormorant Garamond', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                Entre la vision et la r&eacute;alit&eacute; se trouvent le travail, la discipline et la constance. Soyons patients, soyons humbles.
              </p>
              <p className="relative text-sm xl:text-base mt-4 font-extrabold uppercase tracking-[0.25em]" style={{ color: "#C9A25E" }}>— Dr KOBOU DIDIER</p>
            </div>
          </div>

          {/* Separator */}
          <div className="w-20 h-[2px] rounded-full mb-6" style={{ background: "#C9A25E" }} />

          {/* Description */}
          <p className="text-sm mb-5 max-w-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
            La suite logicielle au service des pharmacies de C&ocirc;te d'Ivoire.
          </p>

          {/* Features list */}
          <div className="space-y-2.5 max-w-lg mb-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "rgba(201,162,94,0.10)", border: "1px solid rgba(201,162,94,0.25)" }}
                >
                  <f.icon className="w-4 h-4" style={{ color: "#C9A25E" }} />
                </div>
                <span className="text-sm xl:text-[15px] leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                  {f.text}
                </span>
              </div>
            ))}
          </div>

          {/* Bottom: 3 services summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 rounded-xl text-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,162,94,0.20)" }}>
              <Shield className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#C9A25E" }} />
              <p className="text-[11px] font-bold tracking-wider" style={{ color: "#fff" }}>PHARMA P&Eacute;RIM&Eacute;S</p>
              <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>IA pr&eacute;dictive · Stocks</p>
            </div>
            <div className="p-4 rounded-xl text-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,162,94,0.20)" }}>
              <TrendingUp className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#C9A25E" }} />
              <p className="text-[11px] font-bold tracking-wider" style={{ color: "#fff" }}>PHARMA ASSURANCES</p>
              <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>Recouvrement &amp; Paiements</p>
            </div>
            <div className="p-4 rounded-xl text-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,162,94,0.20)" }}>
              <Zap className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#C9A25E" }} />
              <p className="text-[11px] font-bold tracking-wider" style={{ color: "#fff" }}>PHARMA DISPENSATION</p>
              <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>Copilot Officinal IA</p>
            </div>
          </div>

        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div
        className="flex-1 flex items-center justify-center p-6 md:p-8"
        style={{ background: "#F9F8F6", fontFamily: "'Manrope', sans-serif" }}
      >
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="CMES" className="h-20 w-auto mb-4" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <span style={{ color: "#025946" }}>CMES</span>{" "}
              <span style={{ color: "#C9A25E" }}>Pharma Intel</span>
            </h1>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold mt-2" style={{ color: "#025946" }}>─── Trois services, une seule vision</p>
            <button onClick={() => setShowVideo(true)} data-testid="mobile-play-video-btn"
              className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold"
              style={{ background: "#025946", color: "#fff" }}>
              <PlayCircle className="w-4 h-4" />
              Voir la pr&eacute;sentation
            </button>
          </div>

          {/* Login Card */}
          <Card className="bg-white shadow-xl rounded-2xl" style={{ borderColor: "#E8E4DA" }}>
            <CardHeader className="space-y-1 pb-6 border-b" style={{ borderColor: "#E8E4DA" }}>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.3em]" style={{ color: "#025946" }}>─── Espace pharmacien</p>
              <CardTitle className="text-3xl pt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#0F172A", fontWeight: 400, letterSpacing: "-0.01em" }}>Connexion</CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Acc&eacute;dez &agrave; votre espace CMES Pharma Intel.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {otpStep ? (
                <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="otp-form">
                  <div className="text-center mb-2">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-100 mb-3">
                      <svg className="w-7 h-7 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">Code de verification</h3>
                    <p className="text-xs text-slate-500 mt-1">Code envoye sur <span className="font-mono text-slate-700">{otpStep.emailHint}</span></p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Valable 10 minutes</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-slate-700 text-xs uppercase tracking-wider font-semibold">Code a 6 chiffres</Label>
                    <Input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      autoFocus
                      data-testid="otp-input"
                      className="bg-white text-slate-800 text-2xl tracking-[0.5em] text-center font-mono h-14 rounded-lg"
                      style={{ borderColor: "#E8E4DA" }}
                    />
                  </div>
                  <Button type="submit" disabled={loading || otpCode.length !== 6} className="w-full bg-sky-600 hover:bg-sky-500 text-white h-11 font-semibold" data-testid="verify-otp-btn">
                    {loading ? "Verification..." : "Valider le code"}
                  </Button>
                  <button type="button" onClick={() => { setOtpStep(null); setOtpCode(""); }} className="w-full text-xs text-slate-500 hover:text-slate-700 underline" data-testid="cancel-otp-btn">
                    Annuler et retourner a la connexion
                  </button>
                </form>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 text-xs uppercase tracking-wider font-semibold">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="pharmacie@exemple.ci"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                    className="bg-white text-slate-800 placeholder:text-slate-400 h-11 rounded-lg"
                    style={{ borderColor: "#E8E4DA" }}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 text-xs uppercase tracking-wider font-semibold">Mot de passe</Label>
                    <Link to="/forgot-password" className="text-xs text-slate-600 hover:text-emerald-700 hover:underline font-medium" data-testid="forgot-password-link">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="........"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="bg-white text-slate-800 placeholder:text-slate-400 pr-10 h-11 rounded-lg"
                      style={{ borderColor: "#E8E4DA" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full font-bold py-6 text-sm rounded-full shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5"
                  style={{ background: "#025946", color: "#fff" }}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Se connecter
                    </>
                  )}
                </Button>
              </form>
              )}

              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm">
                  Pas encore de compte ?{" "}
                  <Link
                    to="/register"
                    data-testid="register-link"
                    className="font-bold hover:underline"
                    style={{ color: "#025946" }}
                  >
                    Cr&eacute;er un compte
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* QR Code - Always visible */}
          <Card className="mt-5 shadow-lg overflow-hidden" data-testid="pwa-install-banner"
            style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.12), rgba(139,92,246,0.12))", borderColor: "rgba(14,165,233,0.3)" }}>
            <CardContent className="p-4">
              <div className="flex gap-4 items-center">
                <div className="bg-white p-2.5 rounded-xl flex-shrink-0 shadow-md" data-testid="qr-code-container">
                  <QRCodeSVG value={window.location.origin + "/login"} size={90} level="M"
                    bgColor="#ffffff" fgColor="#0f172a" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-4 h-4" style={{ color: "#0ea5e9" }} />
                    <p className="font-bold text-sm" style={{ color: "#0ea5e9" }}>Accedez depuis votre téléphone</p>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-2">
                    Scannez ce QR code pour ouvrir l'application sur votre téléphone et l'installer.
                  </p>
                  {!isStandalone && (
                    <>
                      {installPrompt ? (
                        <Button onClick={handleInstall} className="text-xs font-bold h-8 px-4" data-testid="install-pwa-btn"
                          style={{ background: "linear-gradient(135deg, #0ea5e9, #8b5cf6)" }}>
                          <Download className="w-3.5 h-3.5 mr-1.5" /> Installer l'app
                        </Button>
                      ) : (
                        <div className="flex gap-3">
                          <span className="text-[10px] text-slate-500"><span className="font-semibold text-slate-300">Android :</span> Menu &#8942; &rarr; Installer</span>
                          <span className="text-[10px] text-slate-500"><span className="font-semibold text-slate-300">iPhone :</span> Partager &rarr; Ecran d'accueil</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Video Presentation */}
          <Card className="mt-5 shadow-lg overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(0,50,70,0.95), rgba(0,130,160,0.85))", borderColor: "rgba(0,180,216,0.4)" }}>
            <CardContent className="p-4">
              <button onClick={() => setShowVideo(true)} data-testid="hero-play-video-btn"
                className="w-full flex items-center gap-4 text-left group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ background: "rgba(255,195,0,0.25)", border: "1px solid rgba(255,195,0,0.5)" }}>
                  <PlayCircle className="w-6 h-6" style={{ color: "#FFC300" }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Decouvrez CMES en video</p>
                  <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.7)" }}>Presentation complete des 3 services — 3 min 30</p>
                </div>
              </button>
            </CardContent>
          </Card>

          {/* Urgent Contact */}
          <Card className="mt-5 shadow-md" style={{ background: "rgba(255,195,0,0.08)", borderColor: "rgba(255,195,0,0.25)" }}>
            <CardContent className="p-4">
              <p className="font-semibold text-sm mb-3" style={{ color: "#B8860B" }}>Besoin d'aide urgente ?</p>
              <div className="flex gap-2">
                <a
                  href="tel:+2250768127000"
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all hover:shadow-md"
                  style={{ background: "#00B4D8", color: "#fff" }}
                >
                  <Phone className="w-4 h-4" />
                  07 68 12 70 00
                </a>
                <a
                  href="https://wa.me/2250768127000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 py-2.5 rounded-lg text-sm font-medium transition-all hover:shadow-md hover:bg-green-700"
                  style={{ color: "#fff" }}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-slate-400 text-xs mt-6">
            CMES — Coaching Medical et Épanouissement Social
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
